import {
  Flex,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Icon,
  useColorModeValue,
} from "@chakra-ui/react";
import React, { useMemo } from "react";
import {
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from "react-table";

// Custom components
import Card from "components/card/Card";
import Menu from "components/menu/MainMenu";
import { CheckCircle2, PauseCircle } from "lucide-react";

export default function CheckTable(props) {
  const { columnsData, tableData, title, menu, onRowClick } = props;

  const columns = useMemo(() => columnsData, [columnsData]);
  const data = useMemo(() => tableData, [tableData]);

  const tableInstance = useTable(
    {
      columns,
      data,
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    initialState,
  } = tableInstance;
  initialState.pageSize = 11;

  const textColor = useColorModeValue("secondaryGray.900", "white");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");
  const rowBorderColor = useColorModeValue("gray.100", "whiteAlpha.100");
  const headerBorderColor = useColorModeValue("#E6ECFA", "rgba(135, 140, 189, 0.3)");
  const theadBg = useColorModeValue("#fbfbfb", "whiteAlpha.50");
  const hoverBg = useColorModeValue("gray.50", "whiteAlpha.100");
  return (
    <Card
      direction='column'
      w='100%'
      h='100%'
      p='0'
      overflowX={{ sm: "scroll", lg: "hidden" }}>
      <Flex justify='space-between' align='center' pt='20px' pb='16px' px='24px' borderBottom='1px solid' borderColor={headerBorderColor}>
        <Text
          color='secondaryGray.900'
          fontSize='18px'
          fontWeight='700'
          lineHeight='100%'
          >
            {title}
        </Text>
        {menu && ( <Menu /> )}
      </Flex>
      <Table {...getTableProps()} variant='simple' color='gray.500' mb='0'>
        <Thead>
          {headerGroups.map((headerGroup, index) => (
            <Tr {...headerGroup.getHeaderGroupProps()} key={index} bg={theadBg} borderTop='1px solid' borderColor={headerBorderColor}>
              {headerGroup.headers.map((column, index) => (
                <Th
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                  ps={index === 0 ? '24px' : undefined}
                  pe={index === headerGroup.headers.length - 1 ? '24px' : '10px'}
                  py='14px'
                  key={index}
                  borderColor={borderColor}
                  textTransform='none'>
                  <Flex
                    align='center'
                    fontSize='sm'
                    fontWeight='600'
                    color={textColor}>
                    {column.render("Header")}
                  </Flex>
                </Th>
              ))}
            </Tr>
          ))}
        </Thead>
        <Tbody {...getTableBodyProps()}>
          {page.map((row, index) => {
            prepareRow(row);
            return (
              <Tr
                {...row.getRowProps()}
                key={index}
                onClick={() => onRowClick && onRowClick(row.original)}
                onKeyDown={(e) => {
                  if (!onRowClick) return;
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onRowClick(row.original);
                  }
                }}
                role={onRowClick ? 'button' : undefined}
                tabIndex={onRowClick ? 0 : undefined}
                cursor={onRowClick ? 'pointer' : 'default'}
                _hover={onRowClick ? { bg: hoverBg } : undefined}
              >
                {row.cells.map((cell, index) => {
                  let data = "";
                  if (cell.column.Header === "Stato" || cell.column.Header === "Status") {
                    data = (
                      <Flex align='center'>
                        <Icon
                        w='24px'
                        h='24px'
                        me='5px'
                        color={
                            cell.value === "completed"
                            ? "green.500"
                            : cell.value === "pending"
                            ? "orange.500"
                            : null
                        }
                        as={
                            cell.value === "completed"
                            ? CheckCircle2
                            : cell.value === "pending"
                            ? PauseCircle
                            : null
                        }
                        />
                    </Flex>
                    );
                  }else{
                    data = (
                        <Flex align='center'>
                          <Text color={textColor} fontSize='sm' textTransform={"capitalize"}>
                            {cell.value}
                          </Text>
                        </Flex>
                      );
                  }
                  return (
                    <Td
                      {...cell.getCellProps()}
                      key={index}
                      fontSize={{ sm: "14px" }}
                      minW={{ sm: "150px", md: "200px", lg: "auto" }}
                      py='14px'
                      ps={index === 0 ? '24px' : undefined}
                      pe={index === row.cells.length - 1 ? '24px' : undefined}
                      borderColor={rowBorderColor}>
                      {data}
                    </Td>
                  );
                })}
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </Card>
  );
}
